import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import TagSelector from './TagSelector';
import ReceiptDataPanel from './shared/ReceiptDataPanel';

const ReviewTab: React.FC = () => {
  const [pending, setPending] = useState<any[]>([]);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const prevObjectUrl = useRef<string | null>(null);

  // Filter States
  const [allDocs, setAllDocs] = useState(false);
  const [filterMonth, setFilterMonth] = useState<string>("");
  const [filterYear, setFilterYear] = useState<string>("");
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [filterMerchant, setFilterMerchant] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  // Tags für das aktuell bearbeitete Dokument
  const [editTags, setEditTags] = useState<string[]>([]);

  useEffect(() => {
    api.get('/tags').then((r) => setAvailableTags(r.data)).catch(() => {});
  }, []);

  const fetchPending = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const res = await api.get('/pending_reviews', {
        params: {
          all_docs: allDocs,
          month: filterMonth || undefined,
          year: filterYear || undefined,
          tags: filterTags.length > 0 ? filterTags.join(',') : undefined,
          merchant: filterMerchant.trim() || undefined,
        }
      });
      setPending(res.data);
      setSelectedIds([]);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || String(err);
      setFetchError(`Fehler beim Laden: ${msg}`);
      console.error("Fehler beim Laden der Freigaben", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); }, [allDocs, filterMonth, filterYear, filterTags, filterMerchant]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = async (item: any) => {
    setSelectedItem(item);
    setEditTags(item.tags || []);
    setSourceUrl(null);
    setZoom(1);
    if (prevObjectUrl.current) {
      URL.revokeObjectURL(prevObjectUrl.current);
      prevObjectUrl.current = null;
    }
    try {
      const res = await api.get(`/get_file?path=${encodeURIComponent(item.minio_path)}`, { responseType: 'blob' });
      const objectUrl = URL.createObjectURL(res.data);
      prevObjectUrl.current = objectUrl;
      setSourceUrl(objectUrl);
    } catch (e) {
      console.error('Datei konnte nicht geladen werden', e);
    }
  };

  const isImageFile = (path?: string) => {
    return path?.toLowerCase().match(/\.(jpg|jpeg|png|webp|bmp|tiff)$/);
  };

  const handleStartAnalysis = async (id: number) => {
    setLoading(true);
    try {
      await api.post(`/process_receipt/${id}`);
      fetchPending();
    } catch (err) {
      alert("Fehler bei der KI-Analyse.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Beleg wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.")) return;
    try {
      await api.delete(`/receipt/${id}`);
      fetchPending();
    } catch (err: any) {
      if (err.response?.status === 403) {
        alert("Freigegebene Belege können nicht gelöscht werden.");
      } else {
        alert("Löschen fehlgeschlagen.");
      }
    }
  };

  const handleUpdate = async () => {
    if (!selectedItem) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('data_json', JSON.stringify(selectedItem.data));
      formData.append('tags_json', JSON.stringify(editTags));
      await api.post(`/update_receipt/${selectedItem.id}`, formData);
      alert("Dokument erfolgreich geprüft und gespeichert.");
      setSelectedItem(null);
      fetchPending();
    } catch (err) {
      alert("Fehler beim Aktualisieren.");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (id: number) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === pending.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pending.map(p => p.id));
    }
  };

  const handleExport = async () => {
    if (selectedIds.length === 0) return;
    setLoading(true);
    try {
      const response = await api.post('/export_receipts', selectedIds, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ensight_export_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Export fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const summaryDocs = selectedIds.length > 0
    ? pending.filter(p => selectedIds.includes(p.id))
    : pending;

  const totalSum = summaryDocs.reduce((acc, item) => acc + (item.total_amount || 0), 0);
  const vatSum   = summaryDocs.reduce((acc, item) => acc + (parseFloat(item.data?.tax_amount) || 0), 0);

  const months = [
    { v: "1", n: "Januar" }, { v: "2", n: "Februar" }, { v: "3", n: "März" },
    { v: "4", n: "April" }, { v: "5", n: "Mai" }, { v: "6", n: "Juni" },
    { v: "7", n: "Juli" }, { v: "8", n: "August" }, { v: "9", n: "September" },
    { v: "10", n: "Oktober" }, { v: "11", n: "November" }, { v: "12", n: "Dezember" }
  ];

  return (
    <div className="w-full max-w-6xl">
      {!selectedItem ? (
        <div className="space-y-4">
          {/* Filter Bar */}
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200 flex flex-wrap gap-4 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase font-bold text-gray-400">Status</label>
              <select value={allDocs ? "true" : "false"} onChange={(e) => setAllDocs(e.target.value === "true")} className="border rounded px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-orange-500">
                <option value="false">Nur Offene</option>
                <option value="true">Alle Dokumente</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase font-bold text-gray-400">Monat</label>
              <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="border rounded px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-orange-500">
                <option value="">Alle Monate</option>
                {months.map(m => <option key={m.v} value={m.v}>{m.n}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase font-bold text-gray-400">Jahr</label>
              <input type="number" value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className="border rounded px-2 py-1 text-sm w-20 outline-none focus:ring-2 focus:ring-orange-500" placeholder="Jahr" />
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
              <label className="text-[10px] uppercase font-bold text-gray-400">Tags</label>
              <TagSelector
                selectedTags={filterTags}
                onChange={setFilterTags}
                availableTags={availableTags}
                placeholder="Nach Tags filtern..."
              />
            </div>
            <div className="flex flex-col gap-1 min-w-[160px]">
              <label className="text-[10px] uppercase font-bold text-gray-400">Rechnungssteller</label>
              <input
                type="text"
                value={filterMerchant}
                onChange={(e) => setFilterMerchant(e.target.value)}
                className="border rounded px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="z.B. OpenAI"
              />
            </div>
            <div className="flex-shrink-0"></div>
            <button onClick={handleExport} disabled={selectedIds.length === 0 || loading} className={`px-4 py-1.5 rounded text-sm font-bold transition ${selectedIds.length > 0 ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
              Export ({selectedIds.length}) 📥
            </button>
          </div>

          {/* Fehleranzeige */}
          {fetchError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
              {fetchError}
            </div>
          )}

          {/* Summary bar */}
          {pending.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg px-5 py-3 flex flex-wrap gap-6 items-center text-sm">
              <span className="text-orange-700 font-semibold">
                {selectedIds.length > 0
                  ? `${selectedIds.length} von ${pending.length} ausgewählt`
                  : `${pending.length} Dokument${pending.length !== 1 ? 'e' : ''}`}
              </span>
              <div className="flex gap-6">
                <span className="text-gray-600">
                  Brutto gesamt:{' '}
                  <span className="font-bold text-gray-900">{totalSum.toFixed(2)} €</span>
                </span>
                <span className="text-gray-600">
                  MwSt gesamt:{' '}
                  <span className="font-bold text-gray-900">{vatSum.toFixed(2)} €</span>
                </span>
                <span className="text-gray-500 text-xs self-center">
                  Netto: {(totalSum - vatSum).toFixed(2)} €
                </span>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-gray-50 text-gray-600 text-xs uppercase font-bold">
                <th className="px-6 py-3 w-10">
                  <input type="checkbox" checked={pending.length > 0 && selectedIds.length === pending.length} onChange={toggleSelectAll} className="rounded text-orange-600 focus:ring-orange-500" />
                </th>
                <th className="px-6 py-3">Händler</th>
                <th className="px-6 py-3">Datum</th>
                <th className="px-6 py-3">Betrag</th>
                <th className="px-6 py-3">Tags</th>
                <th className="px-6 py-3">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pending.length === 0 && !loading && <tr><td colSpan={6} className="p-10 text-center text-gray-400">Keine Dokumente gefunden.</td></tr>}
              {pending.map(item => (
                <tr key={item.id} className={`transition cursor-pointer ${selectedIds.includes(item.id) ? 'bg-orange-50' : 'hover:bg-gray-50'}`} onClick={() => handleSelect(item)}>
                  <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleSelection(item.id)} className="rounded text-orange-600 focus:ring-orange-500" />
                  </td>
                  <td className="px-6 py-4 font-medium">{item.merchant_name || 'Unbekannt'}</td>
                  <td className="px-6 py-4 text-sm">{item.transaction_date}</td>
                  <td className="px-6 py-4 text-sm font-semibold">{item.total_amount?.toFixed(2)} €</td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(item.tags || []).map((tag: string) => (
                        <span key={tag} className="bg-orange-100 text-orange-700 text-[10px] px-2 py-0.5 rounded-full font-medium">{tag}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {!item.data ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleStartAnalysis(item.id); }}
                          disabled={loading}
                          className={`text-blue-600 font-bold hover:underline ${loading ? 'opacity-50 animate-pulse' : ''}`}
                        >
                          {loading ? 'Analysiere...' : 'Analyse starten ✨'}
                        </button>
                      ) : (
                        <>
                          <span className={`text-xs font-bold px-2 py-1 rounded ${item.reviewed ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                            {item.reviewed ? 'Geprüft ✓' : 'Prüfen →'}
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleStartAnalysis(item.id); }}
                            disabled={loading}
                            title="Erneut analysieren"
                            className="text-gray-400 hover:text-blue-600 transition text-base disabled:opacity-40"
                          >
                            ↺
                          </button>
                        </>
                      )}
                      {!item.reviewed && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                          title="Beleg löschen"
                          className="text-gray-300 hover:text-red-500 transition text-base ml-1"
                        >
                          🗑
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      ) : (
        <div className="mt-8 flex flex-col lg:flex-row gap-6 items-start">
          <div className="w-full lg:w-1/2 bg-white rounded-lg shadow-lg border border-gray-200 h-[600px] flex flex-col overflow-hidden">
            <div className="bg-gray-100 px-4 py-3 border-b flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-600">Rechnungs-Original</span>
              <button onClick={() => setSelectedItem(null)} className="text-xs text-gray-500 underline">Zurück zur Liste</button>
            </div>
            <div className="flex-1 overflow-hidden flex flex-col bg-gray-50 relative group">
              {isImageFile(selectedItem.minio_path) ? (
                <>
                  <div className="absolute top-4 right-4 flex gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setZoom(z => Math.max(z - 0.2, 0.5))} className="bg-white border shadow-sm px-3 py-1 rounded-l hover:bg-gray-50 text-gray-700 font-bold">-</button>
                    <button onClick={() => setZoom(1)} className="bg-white border-y shadow-sm px-2 py-1 hover:bg-gray-50 text-xs text-gray-600 min-w-[50px]">{Math.round(zoom * 100)}%</button>
                    <button onClick={() => setZoom(z => Math.min(z + 0.2, 4))} className="bg-white border shadow-sm px-3 py-1 rounded-r hover:bg-gray-50 text-gray-700 font-bold">+</button>
                  </div>
                  <div className="flex-1 overflow-auto flex items-center justify-center">
                    <img src={sourceUrl!} style={{ transform: `scale(${zoom})`, transition: 'transform 0.15s ease-out' }} className="max-w-full max-h-full object-contain shadow-md origin-center" alt="Vorschau" />
                  </div>
                </>
              ) : (
                <iframe src={sourceUrl!} className="w-full h-full" title="Preview" />
              )}
            </div>
          </div>

          <div className="w-full lg:w-1/2">
            {!selectedItem.data ? (
              <div className="bg-white rounded-lg shadow border p-10 text-center">
                <p className="text-gray-500 mb-4">Für dieses Dokument liegen noch keine Analysedaten vor.</p>
                <button
                  onClick={() => handleStartAnalysis(selectedItem.id)}
                  disabled={loading}
                  className={`bg-blue-600 text-white px-6 py-2 rounded shadow hover:bg-blue-700 transition flex items-center gap-2 mx-auto ${loading ? 'opacity-70 cursor-wait' : ''}`}
                >
                  {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>}
                  {loading ? 'Extrahiere Daten...' : 'KI-Analyse jetzt starten'}
                </button>
              </div>
            ) : (
              <ReceiptDataPanel
                data={selectedItem.data}
                tags={editTags}
                availableTags={availableTags}
                onChange={(updated) => setSelectedItem({ ...selectedItem, data: updated })}
                onTagsChange={setEditTags}
                headerTitle="Daten validieren"
                headerColor="bg-orange-600"
                footer={
                  <>
                    <button onClick={() => setSelectedItem(null)} className="px-4 py-2 text-gray-600">Abbrechen</button>
                    <button onClick={handleUpdate} disabled={loading} className="bg-orange-600 text-white px-6 py-2 rounded hover:bg-orange-700 shadow-md transition">
                      {loading ? 'Speichere...' : 'Freigeben & In DMS speichern'}
                    </button>
                  </>
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewTab;